/**
 * Directions on one page (owner, 2026-10-12: "я не розумію де я можу напрями змінювати/додавати… все дуже
 * муторно і заплутано"). Before this, adding one lived at the bottom of Accounts and binding a phone on
 * Machines, one phone at a time. Here: what a direction is, each one with its phones and accounts, the
 * admin's add and rename, and the phones without a direction — ticked several at once and bound in one go.
 *
 * Binding is still **for ever** (the service refuses another direction later), so it goes through the same
 * hold-to-confirm dialog, which names every phone it binds.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import type { Device, Direction, MarketingAccount } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { Card, Status } from '../../ui/components'
import { DirectionChip, useDirectionName, useDirections } from './directions'

function errorText(e: unknown): string {
  const d = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
  return d?.hint ?? (e instanceof Error ? e.message : String(e))
}

const LATIN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'h', ґ: 'g', д: 'd', е: 'e', є: 'ie', ж: 'zh', з: 'z', и: 'y', і: 'i', ї: 'i',
  й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh',
  ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch', ь: '', ю: 'iu', я: 'ia', ы: 'y', э: 'e', ъ: '', ё: 'io',
}

/** The id the service wants (2–31 latin letters, digits, hyphens), made from the name a person typed —
 *  nobody has to invent one. "Казино-слоти" → "kazyno-sloty"; taken → "-2", "-3"… */
export function directionIdFrom(name: string, taken: Set<string>): string {
  const latin = [...name.toLowerCase()].map((c) => LATIN[c] ?? c).join('')
  let base = latin.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 26)
  if (!/^[a-z]/.test(base)) base = `dir-${base}`.replace(/-+$/, '')
  if (base.length < 2) base = 'dir'
  let id = base
  for (let i = 2; taken.has(id); i++) id = `${base}-${i}`
  return id
}

const phoneName = (d: Device) => d.name || d.model || d.serial

function usePhonesAndAccounts() {
  const { api, authEpoch } = useApp()
  const dirs = useDirections()
  const devices = usePoll(() => api.devices(), 30000, [api, authEpoch])
  const accounts = usePoll(() => api.accounts(), 60000, [api, authEpoch])
  const bySerial = useMemo(() => {
    const out = new Map<string, MarketingAccount[]>()
    for (const a of accounts.data?.accounts ?? []) out.set(a.serial, [...(out.get(a.serial) ?? []), a])
    return out
  }, [accounts.data])
  const refresh = () => {
    dirs.refresh()
    devices.refresh()
    accounts.refresh()
  }
  return { dirs, all: devices.data ?? [], bySerial, refresh }
}

/** One direction's phones (its "Phones" tab), with the phones still without a direction to bind. */
export function DirectionPhones({ direction }: { direction: Direction }) {
  const { dirs, all, bySerial, refresh } = usePhonesAndAccounts()
  return (
    <>
      <DirectionCard direction={direction} phones={all.filter((p) => p.direction_id === direction.direction_id)}
                     bySerial={bySerial} colors={dirs.data?.colors ?? []} onChanged={refresh} />
      <Unbound phones={all.filter((d) => !d.direction_id)} directions={dirs.list} bySerial={bySerial} onDone={refresh} />
    </>
  )
}

export function DirectionsPanel() {
  const t = useT()
  const m = t.dirs
  const { auth } = useApp()
  const { dirs, all, bySerial, refresh } = usePhonesAndAccounts()
  const unbound = all.filter((d) => !d.direction_id)
  return (
    <div className="col dirs-page">
      <Card title={m.title} sub={m.sub}>
        <ul className="dirs-rules tiny">
          {m.rules.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
        {auth.isAdmin && <AddDirection taken={new Set(dirs.list.map((d) => d.direction_id))} onDone={refresh} />}
      </Card>
      {dirs.list.map((d) => (
        <DirectionCard key={d.direction_id} direction={d} phones={all.filter((p) => p.direction_id === d.direction_id)}
                       bySerial={bySerial} colors={dirs.data?.colors ?? []} onChanged={refresh} />
      ))}
      <Unbound phones={unbound} directions={dirs.list} bySerial={bySerial} onDone={refresh} />
    </div>
  )
}

function AddDirection({ taken, onDone }: { taken: Set<string>; onDone: () => void }) {
  const m = useT().dirs
  const { api } = useApp()
  const colors = useDirections().data?.colors ?? []
  const [name, setName] = useState('')
  const [color, setColor] = useState('teal')
  const [problem, setProblem] = useState<string | null>(null)
  const add = async () => {
    setProblem(null)
    try {
      await api.addDirection({ direction_id: directionIdFrom(name, taken), name: name.trim(), color })
      setName('')
      onDone()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  return (
    <div className="dirs-add">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.newName} aria-label={m.newName}
             maxLength={40} onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) void add() }} />
      <ColorPick colors={colors} value={color} onChange={setColor} />
      <button className="go" disabled={!name.trim()} onClick={() => void add()}>{m.add}</button>
      <Status text={problem} bad />
    </div>
  )
}

function ColorPick({ colors, value, onChange }: { colors: string[]; value: string; onChange: (c: string) => void }) {
  const m = useT().dirs
  return (
    <span className="color-pick" role="radiogroup" aria-label={m.color}>
      {colors.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} aria-label={c} title={c}
                className="color-swatch" data-color={c} onClick={() => onChange(c)} />
      ))}
    </span>
  )
}

function DirectionCard({ direction: d, phones, bySerial, colors, onChanged }: {
  direction: Direction
  phones: Device[]
  bySerial: Map<string, MarketingAccount[]>
  colors: string[]
  onChanged: () => void
}) {
  const t = useT()
  const m = t.dirs
  const nameOf = useDirectionName()
  const { api, auth } = useApp()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(nameOf(d))
  const [color, setColor] = useState(d.color)
  const [problem, setProblem] = useState<string | null>(null)
  const save = async () => {
    setProblem(null)
    try {
      await api.editDirection(d.direction_id, { name: name.trim(), color })
      setEditing(false)
      onChanged()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  const accounts = phones.reduce((n, p) => n + (bySerial.get(p.serial)?.length ?? 0), 0)
  return (
    <Card className="dir-tint" color={d.color}
          title={<Link to={`/directions/${encodeURIComponent(d.direction_id)}`} className="dir-link"><DirectionChip direction={d} /> →</Link>}
          sub={m.counts(phones.length, accounts)}
          actions={auth.isAdmin && !editing
            ? <button className="quiet" onClick={() => { setName(nameOf(d)); setColor(d.color); setEditing(true) }}>{m.edit}</button>
            : undefined}>
      {editing && (
        <div className="dirs-add">
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label={m.newName} maxLength={40} />
          <ColorPick colors={colors} value={color} onChange={setColor} />
          <button className="go" disabled={!name.trim()} onClick={() => void save()}>{m.save}</button>
          <button className="quiet" onClick={() => setEditing(false)}>{m.cancel}</button>
          <Status text={problem} bad />
        </div>
      )}
      {phones.length === 0
        ? <div className="tiny dim">{m.noPhones}</div>
        : <PhoneRows phones={phones} bySerial={bySerial} color={d.color} />}
    </Card>
  )
}

function PhoneRows({ phones, bySerial, color, picked, onPick }: {
  phones: Device[]
  bySerial: Map<string, MarketingAccount[]>
  color?: string
  picked?: Set<string>
  onPick?: (id: string) => void
}) {
  const m = useT().dirs
  return (
    <ul className="dirs-phones">
      {[...phones].sort((a, b) => phoneName(a).localeCompare(phoneName(b), undefined, { numeric: true })).map((p) => {
        const accs = bySerial.get(p.serial) ?? []
        const label = (
          <>
            <b>{phoneName(p)}</b>
            <span className="tiny dim mono">{p.name && p.model ? `${p.model} · ` : ''}{p.serial}</span>
            {p.hub_id && <span className="tiny dim">· {p.hub_id}</span>}
            <span className={`phone-status ${p.status}`}>{m.status[p.status] ?? p.status}</span>
            <span className="tiny dirs-accs">
              {accs.length
                ? accs.map((a) => <Link key={a.account_id} to={`/accounts/${encodeURIComponent(a.account_id)}`} className="acc-pill" data-color={color}>{a.email || `@${a.handle}`}</Link>)
                : <span className="dim">{m.noAccounts}</span>}
            </span>
          </>
        )
        return (
          <li key={p.device_id} className="dirs-phone">
            {onPick
              ? <label className="dirs-pick"><input type="checkbox" checked={picked?.has(p.device_id) ?? false}
                                                     onChange={() => onPick(p.device_id)} />{label}</label>
              : <span className="dirs-pick">{label}</span>}
          </li>
        )
      })}
    </ul>
  )
}

function Unbound({ phones, directions, bySerial, onDone }: {
  phones: Device[]
  directions: Direction[]
  bySerial: Map<string, MarketingAccount[]>
  onDone: () => void
}) {
  const t = useT()
  const m = t.dirs
  const nameOf = useDirectionName()
  const { api, auth } = useApp()
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [target, setTarget] = useState('')
  const [asking, setAsking] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const chosen = directions.find((d) => d.direction_id === target)
  const toggle = (id: string) => setPicked((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    return n
  })
  const pickedPhones = phones.filter((p) => picked.has(p.device_id))
  const bind = async () => {
    if (!chosen) return
    setProblem(null)
    const failed: string[] = []
    for (const p of pickedPhones) {
      try {
        await api.bindDirection(p.device_id, chosen.direction_id)
      } catch (e) {
        failed.push(`${phoneName(p)}: ${errorText(e)}`)
      }
    }
    setAsking(false)
    setPicked(new Set())
    if (failed.length) setProblem(failed.join(' · '))
    onDone()
  }
  if (phones.length === 0) return <Card className="dir-none" title={m.unboundTitle(0)} sub={m.allBound}><span /></Card>
  return (
    <Card className="dir-none" title={m.unboundTitle(phones.length)} sub={auth.isAdmin ? m.unboundSubAdmin : m.unboundSub}>
      {auth.isAdmin && (
        <div className="dirs-bindbar">
          <button className="quiet" onClick={() => setPicked(picked.size === phones.length ? new Set() : new Set(phones.map((p) => p.device_id)))}>
            {picked.size === phones.length ? m.pickNone : m.pickAll}
          </button>
          <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label={m.toDirection}>
            <option value="">{m.toDirection}</option>
            {directions.map((d) => <option key={d.direction_id} value={d.direction_id}>{nameOf(d)}</option>)}
          </select>
          <button className="go" disabled={!chosen || picked.size === 0} onClick={() => setAsking(true)}>
            {m.bind(picked.size)}
          </button>
        </div>
      )}
      <Status text={problem} bad />
      <PhoneRows phones={phones} bySerial={bySerial} picked={auth.isAdmin ? picked : undefined}
                 onPick={auth.isAdmin ? toggle : undefined} />
      {asking && chosen && (
        <ConfirmDanger title={m.confirmTitle(pickedPhones.length, nameOf(chosen))} confirmLabel={m.confirm}
                       holdSeconds={3} onCancel={() => setAsking(false)} onConfirm={() => void bind()}>
          <div>{t.marketing.bindForever}</div>
          <div className="tiny">{pickedPhones.map(phoneName).join(', ')}</div>
          <div className="tiny dim">{t.marketing.bindOnlyMarketing}</div>
        </ConfirmDanger>
      )}
    </Card>
  )
}
